import { useMemo, useState } from 'react'

import { useAppConfirm } from '../../../base/contexts/AppConfirmProvider.tsx'
import { useError } from '../../../base/contexts/ErrorProvider.tsx'
import { useToast } from '../../../base/contexts/ToastContext.tsx'
import useAuth from '../../../base/hooks/useAuth.ts'
import { ArticuloInputProps, ArticuloProps } from '../../../interfaces/articulo.ts'
import { getEntidadInput } from '../../../utils/getEntidadInput.ts'
import { swalClose, swalLoading } from '../../../utils/swal.ts'
import { apiArticuloActualizar } from '../api/apiArticuloActualizar.ts'
import { apiArticuloEliminar } from '../api/apiArticuloEliminar.ts'
import { apiArticuloExportar } from '../api/apiArticuloExportar.ts'
import { apiArticuloRegistro } from '../api/apiArticuloRegistro.ts'
import { exportarArticulosCSV } from '../services/articuloExportar.ts'
import { articuloInputToApi } from '../services/articuloInputToApi.ts'

type ActionState =
  | { mode: 'idle'; open: false; data: null }
  | { mode: 'importar'; open: true; data: null }
  | { mode: 'actualizarMasivo'; open: true; data: null }
  | { mode: 'importarImagen'; open: true; data: ArticuloProps }

const initialState: ActionState = { mode: 'idle', open: false, data: null }

/**
 * Operaciones de articulo
 */
export const useArticuloOperacion = () => {
  const { user } = useAuth()
  const entidad = useMemo(() => getEntidadInput(user), [user])
  const [action, setAction] = useState<ActionState>(initialState)

  /** hooks auxiliares */
  const { requestConfirm } = useAppConfirm()
  const { toast } = useToast()
  const { showError } = useError()

  const onActionReset = () => setAction(initialState)
  const onActionImportar = () => setAction({ mode: 'importar', open: true, data: null })
  const onActionImportarImagen = (data: ArticuloProps) =>
    setAction({ mode: 'importarImagen', open: true, data })
  const onActionActualizarMasivo = () => setAction({ mode: 'actualizarMasivo', open: true, data: null })

  /** Exportar datos del data table */
  const exportarDatos = async (update: boolean = false) => {
    swalLoading()
    try {
      const resp = await apiArticuloExportar(1000)
      if (resp.pageInfo.totalDocs === 0) {
        toast.error('No se han encontrado datos para exportación.')
        return
      }
      toast.success(`Se han exportado ${resp.pageInfo.totalDocs} articulos`)
      await exportarArticulosCSV(resp.docs, update)
    } catch (error: any) {
      showError(error)
    } finally {
      swalClose()
    }
  }

  /** Me permite eliminar un registro */
  const onEliminar = async (row: ArticuloProps, onSuccess?: () => void) => {
    const { confirmed } = await requestConfirm({
      confirmButtonColor: 'error',
      title: 'Eliminar Articulo',
      description: `Se eliminará el articulo ${row.codigoArticulo} - ${row.nombreArticulo}?`,
      steps: ['No debe existir stock en ningún almacen'],
    })
    if (!confirmed) return
    swalLoading()
    try {
      await apiArticuloEliminar(row.codigoArticulo)
      toast.success('Registro eliminado correctamente')
      if (onSuccess) onSuccess()
    } catch (e) {
      showError(e)
    } finally {
      swalClose()
    }
  }

  /** Registro de un articulo */
  const onRegistrar = async (data: ArticuloInputProps, onSuccess?: (resp?: ArticuloProps) => void) => {
    const { codigoArticulo, input } = articuloInputToApi(data)
    const { confirmed } = await requestConfirm({
      confirmButtonColor: 'success',
      title: 'Registrar Articulo',
      content: `Se registrará el articulo ${data.codigoArticulo} - ${data.nombreArticulo}?`,
    })
    if (!confirmed) return
    swalLoading()
    try {
      const resp = await apiArticuloRegistro(codigoArticulo, input)
      toast.success('Actualización exitosa')
      if (onSuccess) onSuccess(resp)
    } catch (e) {
      showError(e)
    } finally {
      swalClose()
    }
  }

  /** Actualizacion de un articulo */
  const onActualizar = async (data: ArticuloInputProps, onSuccess?: (resp?: ArticuloProps) => void) => {
    const { codigoArticulo, input } = articuloInputToApi(data)
    // const { confirmed } = await requestConfirm({
    //   confirmButtonColor: 'success',
    //   title: 'Actualizar articulo',
    //   content: `Se actualizará el articulo ${codigoArticulo} - ${data.nombreArticulo}?`,
    // })
    // if (!confirmed) return
    swalLoading()
    try {
      const resp = await apiArticuloActualizar(codigoArticulo, input)
      toast.success('Actualización exitosa')
      if (onSuccess) onSuccess(resp)
    } catch (e) {
      showError(e)
    } finally {
      swalClose()
    }
  }

  return {
    user,
    entidad,
    sucursal: user.sucursal,
    pva: user.puntoVenta,
    onEliminar,
    onActionReset,
    onActionImportar,
    onActionImportarImagen,
    onActionActualizarMasivo,
    exportarDatos,
    onActualizar,
    onRegistrar,
    action,
  }
}
